import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, ClipboardList, Bike, Users, Beer, Truck, FileText,
  BarChart3, MessageSquare, Settings, ChevronDown, ChevronsLeft,
  ChevronsRight, LogOut, Menu as MenuIcon, X, Store, KeyRound, Check,
  CreditCard, Megaphone, ScrollText, Sparkles, MoreHorizontal, CalendarDays,
} from "lucide-react";
import NotificationBell from "./NotificationBell";
import { SyncPill } from "./SyncPill";
import { ThemeToggle } from "./ThemeToggle";
import { initSync } from "../lib/sync";
import { useAuth, useWorkspace } from "./AuthContext";
import { useI18n } from "../lib/i18n";
import { api, formatApiErrorDetail } from "../lib/api";
import { pollMs, useDataSaver } from "../lib/dataSaver";
import { BpButton, BpInput, BpBadge, BpWordmark } from "../ui-kit";

const NAVS = {
  bar: [
    { to: "/app/dashboard", key: "nav.dashboard", icon: LayoutDashboard },
    { to: "/app/orders", key: "nav.orders", icon: ClipboardList },
    { to: "/app/reservations", key: "nav.reservations", icon: CalendarDays, badge: "pending_reservations" },
    { to: "/app/delivery", key: "nav.delivery", icon: Bike },
    { to: "/app/clients", key: "nav.clients", icon: Users },
    { to: "/app/menu", key: "nav.menu", icon: Beer },
    { to: "/app/distributors", key: "nav.distributors", icon: Truck },
    { to: "/app/invoices", key: "nav.invoices", icon: FileText },
    { to: "/app/reports", key: "nav.reports", icon: BarChart3 },
    { to: "/app/messages", key: "nav.messages", icon: MessageSquare },
    { to: "/app/settings", key: "nav.settings", icon: Settings },
  ],
  distributor: [
    { to: "/dist/dashboard", key: "nav.dashboard", icon: LayoutDashboard },
    { to: "/dist/orders", key: "nav.supplyOrders", icon: ClipboardList },
    { to: "/dist/deliveries", key: "nav.deliveries", icon: Truck },
    { to: "/dist/invoices", key: "nav.invoices", icon: FileText },
    { to: "/dist/bars", key: "nav.partnerBars", icon: Store },
    { to: "/dist/catalog", key: "nav.catalog", icon: Beer },
    { to: "/dist/messages", key: "nav.messages", icon: MessageSquare },
    { to: "/dist/settings", key: "nav.settings", icon: Settings },
  ],
  platform: [
    { to: "/admin/overview", key: "nav.overview", icon: LayoutDashboard },
    { to: "/admin/bars", key: "nav.adminBars", icon: Store },
    { to: "/admin/distributors", key: "nav.adminDists", icon: Truck },
    { to: "/admin/accounts", key: "nav.accounts", icon: Users },
    { to: "/admin/subscriptions", key: "nav.subscriptions", icon: CreditCard, badge: "pending_proofs" },
    { to: "/admin/revenue", key: "nav.revenue", icon: BarChart3 },
    { to: "/admin/announcements", key: "nav.announcements", icon: Megaphone, badge: "live_announcements" },
    { to: "/admin/audit", key: "nav.audit", icon: ScrollText },
    { to: "/admin/settings", key: "nav.settings", icon: Settings },
  ],
};

const PRIMARY_TABS = {
  bar: ["/app/dashboard", "/app/orders", "/app/clients", "/app/menu"],
  distributor: ["/dist/dashboard", "/dist/orders", "/dist/invoices", "/dist/bars"],
  platform: ["/admin/overview", "/admin/bars", "/admin/subscriptions", "/admin/accounts"],
};

function BottomTabs({ kind, badges }) {
  const { t } = useI18n();
  const [moreOpen, setMoreOpen] = useState(false);
  const primary = NAVS[kind].filter((n) => PRIMARY_TABS[kind].includes(n.to));
  const rest = NAVS[kind].filter((n) => !PRIMARY_TABS[kind].includes(n.to));
  return (
    <>
      {moreOpen && (
        <div className="fixed inset-0 z-[55] md:hidden" onClick={() => setMoreOpen(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute bottom-14 left-0 right-0 bg-rail text-rail-ink border-t border-rail-ink/10 rounded-t-3xl p-3 pb-4 grid grid-cols-3 gap-1" data-testid="bottom-more-sheet" onClick={(e) => e.stopPropagation()}>
            {rest.map(({ to, key, icon: Icon, badge }) => (
              <NavLink key={to} to={to} onClick={() => setMoreOpen(false)} data-testid={`tab-more-${to.split("/").pop()}`}
                className={({ isActive }) =>
                  `relative min-h-[64px] flex flex-col items-center justify-center gap-1 rounded-2xl text-[11px] font-semibold ` +
                  (isActive ? "text-brand bg-rail-soft" : "text-rail-ink/75")}>
                <Icon size={20} />
                {t(key)}
                {badge && badges[badge] > 0 && (
                  <span className="absolute top-1.5 right-5 text-[9px] font-bold bg-pink text-white rounded-full px-1.5">{badges[badge]}</span>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      )}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-[56] bg-rail-head/95 text-rail-ink backdrop-blur border-t border-rail-ink/10 flex"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="bottom-tabs">
        {primary.map(({ to, key, icon: Icon }) => (
          <NavLink key={to} to={to} onClick={() => setMoreOpen(false)} data-testid={`tab-${to.split("/").pop()}`}
            className={({ isActive }) =>
              `flex-1 min-h-[56px] flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold ` +
              (isActive ? "text-brand" : "text-rail-ink/65")}>
            <Icon size={21} />
            {t(key)}
          </NavLink>
        ))}
        <button onClick={() => setMoreOpen(!moreOpen)} data-testid="tab-more"
          className={`flex-1 min-h-[56px] flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold ${moreOpen ? "text-brand" : "text-rail-ink/65"}`}>
          <MoreHorizontal size={21} />
          {t("nav.more")}
        </button>
      </nav>
    </>
  );
}

function WorkspaceSwitcher({ current }) {
  const { workspaces, switchWorkspace, acceptInvite } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [code, setCode] = useState("");

  const go = async (w) => {
    setOpen(false);
    await switchWorkspace(w.org_id);
    navigate(w.org_type === "bar" ? "/app/dashboard" : w.org_type === "platform" ? "/admin/overview" : "/dist/dashboard");
  };

  const join = async () => {
    try {
      const data = await acceptInvite(code.trim());
      const w = data.workspaces[data.workspaces.length - 1];
      setJoinOpen(false);
      setCode("");
      toast.success(`${w.org_name} ✓`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="relative">
      <button
        className="flex items-center gap-2 rounded-xl bg-rail-soft text-rail-ink px-3 py-2 hover:bg-rail-soft2 transition-colors"
        onClick={() => setOpen(!open)}
        data-testid="workspace-switcher-btn"
      >
        <span className="font-semibold text-sm max-w-[140px] truncate">{current?.org_name}</span>
        <BpBadge tone={current?.org_type === "bar" ? "orange" : "gold"} className={current?.org_type === "bar" ? "text-brand" : "text-gold"}>{t(`role.${current?.role}`)}</BpBadge>
        <ChevronDown size={16} className="text-rail-ink/65" />
      </button>
      {open && (
        <div className="absolute left-0 top-12 z-50 w-72 rounded-2xl bg-surface text-ink border border-line shadow-card p-2" data-testid="workspace-dropdown">
          <p className="px-3 py-1.5 text-xs uppercase tracking-wide text-ink/60">{t("shell.workspaces")}</p>
          {workspaces.map((w) => (
            <button
              key={w.membership_id}
              className="w-full flex items-center justify-between rounded-xl px-3 py-2.5 hover:bg-inset text-left"
              onClick={() => go(w)}
              data-testid={`workspace-option-${w.org_slug}`}
            >
              <span>
                <span className="block font-medium text-sm">{w.org_name}</span>
                <span className="block text-xs text-ink/65">{t(`role.${w.role}`)}</span>
              </span>
              {current?.org_id === w.org_id && <Check size={16} className="text-brand-ink" />}
            </button>
          ))}
          <button
            className="w-full flex items-center gap-2 rounded-xl px-3 py-2.5 hover:bg-inset text-brand-ink text-sm font-medium"
            onClick={() => { setOpen(false); setJoinOpen(true); }}
            data-testid="join-team-btn"
          >
            <KeyRound size={15} /> {t("shell.joinTeam")}
          </button>
        </div>
      )}
      {joinOpen && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setJoinOpen(false)}>
          <div className="bg-surface text-ink rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="join-team-modal">
            <h3 className="font-heading text-xl mb-1">{t("shell.joinTeam")}</h3>
            <p className="text-sm text-ink/70 mb-4">{t("shell.joinTeamDesc")}</p>
            <BpInput value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={6} placeholder="ABC123" className="text-center text-lg tracking-[0.3em] font-bold mb-4" data-testid="join-code-input" />
            <BpButton className="w-full" onClick={join} disabled={code.length !== 6} data-testid="join-submit-btn">{t("shell.join")}</BpButton>
          </div>
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { account, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        className="w-9 h-9 rounded-full bg-brand text-onfill font-bold flex items-center justify-center"
        onClick={() => setOpen(!open)}
        data-testid="user-menu-btn"
      >
        {account?.name?.[0]?.toUpperCase() || "?"}
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-50 w-56 rounded-2xl bg-surface text-ink border border-line shadow-card p-2" data-testid="user-menu-dropdown">
          <div className="px-3 py-2">
            <p className="font-semibold text-sm">{account?.name}</p>
            <p className="text-xs text-ink/65">{account?.phone}</p>
          </div>
          <button
            className="w-full flex items-center gap-2 rounded-xl px-3 py-2.5 hover:bg-inset text-pink-ink text-sm font-medium"
            onClick={() => { logout(); navigate("/login"); }}
            data-testid="logout-btn"
          >
            <LogOut size={15} /> {t("shell.logout")}
          </button>
        </div>
      )}
    </div>
  );
}

export default function Shell({ kind }) {
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const current = useWorkspace(kind);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("bp_sidebar") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [badges, setBadges] = useState({});
  const dataSaver = useDataSaver();
  const [billing, setBilling] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [upsell, setUpsell] = useState(null);
  const impersonating = localStorage.getItem("bp_imp_name");
  const nav = NAVS[kind];

  useEffect(() => {
    const h = (e) => setUpsell(e.detail);
    window.addEventListener("bp:upgrade", h);
    return () => window.removeEventListener("bp:upgrade", h);
  }, []);

  useEffect(() => {
    if (kind === "bar" && current) {
      initSync(current.org_id);
      // preload offline-critical chunks so the shell works without network
      import("../features/venue/ClientsPage");
      import("../features/venue/OrdersFeed");
      import("../features/venue/NewOrderSheet");
      import("../features/catalog/MenuStock");
      import("../features/reporting/ReportsPage");
      import("../features/reporting/BarDashboard");
      import("../features/sync/SyncIssuesPage");
    }
  }, [kind, current]);

  useEffect(() => {
    const h = (e) => {
      const reg = e.detail;
      toast(t("sync.updateReady"), {
        duration: 30000,
        action: { label: t("sync.refresh"), onClick: () => reg.waiting?.postMessage("SKIP_WAITING") },
      });
    };
    window.addEventListener("bp:sw-update", h);
    return () => window.removeEventListener("bp:sw-update", h);
  }, [t]);

  useEffect(() => {
    if (kind !== "platform" || !current) return;
    const load = () => api.get("/platform/badges").then(({ data }) => setBadges(data)).catch(() => {});
    load();
    const id = setInterval(load, pollMs(30000));
    return () => clearInterval(id);
  }, [kind, current, dataSaver]);

  useEffect(() => {
    if (kind !== "bar" || !current) return;
    const load = () => api.get(`/reservations/bars/${current.org_id}/badges`)
      .then(({ data }) => setBadges((b) => ({ ...b, ...data }))).catch(() => {});
    load();
    const id = setInterval(load, pollMs(30000));
    return () => clearInterval(id);
  }, [kind, current, dataSaver]);

  useEffect(() => {
    if (!current || kind === "platform") return;
    api.get("/platform/announcements/active", { params: { org_id: current.org_id } })
      .then(({ data }) => setAnnouncements(data)).catch(() => {});
    if (kind === "bar") {
      api.get(`/billing/orgs/${current.org_id}/subscription`)
        .then(({ data }) => setBilling(data)).catch(() => {});
    }
  }, [kind, current]);

  const toggleCollapse = () => {
    localStorage.setItem("bp_sidebar", collapsed ? "0" : "1");
    setCollapsed(!collapsed);
  };

  const stopImpersonating = async () => {
    try { await api.post("/platform/impersonate/stop"); } catch (e) { console.warn("stop impersonation failed", e); }
    localStorage.removeItem("bp_imp_name");
    window.location.href = "/admin/accounts";
  };

  const status = billing?.subscription?.status;

  const sidebar = (
    <div className={`flex flex-col h-full bg-rail text-rail-ink ${collapsed ? "w-16" : "w-60"} transition-[width] duration-200`}>
      <div className={`flex items-center ${collapsed ? "justify-center" : "px-5"} h-16 shrink-0`}>
        {collapsed ? (
          <img src="/barpadi-icon.png" alt="BarPadi" className="w-9 h-9 rounded-xl" />
        ) : (
          <span className="flex items-center gap-2.5">
            <img src="/barpadi-icon.png" alt="" className="w-9 h-9 rounded-xl" />
            <BpWordmark />
            {kind === "platform" && (
              <span className="text-[10px] font-bold uppercase tracking-wide bg-gold text-onfill rounded-full px-2 py-0.5" data-testid="admin-chip">Admin</span>
            )}
          </span>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto py-4 space-y-0.5">
        {nav.map(({ to, key, icon: Icon, badge }) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${to.split("/").pop()}`}
            className={({ isActive }) =>
              `relative flex items-center gap-3 ${collapsed ? "justify-center px-0" : "px-5"} py-2.5 text-sm font-medium transition-colors ` +
              (isActive ? "text-brand" : "text-rail-ink/70 hover:text-rail-ink")
            }
          >
            {({ isActive }) => (
              <>
                {isActive && <span className="absolute left-0 top-1 bottom-1 w-1 rounded-r bg-brand" />}
                <Icon size={19} />
                {!collapsed && <span className="flex-1">{t(key)}</span>}
                {!collapsed && badge && badges[badge] > 0 && (
                  <span className="text-[10px] font-bold bg-pink text-white rounded-full px-1.5 py-0.5" data-testid={`badge-${badge}`}>{badges[badge]}</span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <button
        className="hidden lg:flex items-center justify-center h-12 text-rail-ink/60 hover:text-brand shrink-0"
        onClick={toggleCollapse}
        data-testid="sidebar-collapse-btn"
      >
        {collapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-app flex">
      <aside className="hidden lg:block sticky top-0 h-screen shrink-0">{sidebar}</aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{sidebar}</div>
          <button className="absolute top-4 right-4 text-rail-ink" onClick={() => setMobileOpen(false)}><X /></button>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 flex items-center gap-3 px-4 sm:px-6 sticky top-0 z-40 bg-rail-head/95 text-rail-ink backdrop-blur">
          <button className="hidden md:block lg:hidden p-2 -ml-2" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <MenuIcon size={22} />
          </button>
          <WorkspaceSwitcher current={current} />
          {kind === "bar" && <SyncPill />}
          <div className="flex-1" />
          <ThemeToggle />
          <button
            className="text-xs font-bold text-rail-ink/70 hover:text-brand px-2 py-1"
            onClick={() => {
              const next = lang === "fr" ? "en" : "fr";
              setLang(next);
              api.post("/identity/me/language", { lang: next }).catch(() => {});
            }}
            data-testid="lang-toggle"
          >
            {lang === "fr" ? "EN" : "FR"}
          </button>
          <NotificationBell />
          <UserMenu />
        </header>
        <main className="flex-1 px-4 sm:px-6 pb-28 md:pb-16 pt-2">
          {impersonating && (
            <div className="rounded-xl bg-gold text-onfill text-sm font-semibold px-4 py-2.5 mb-3 flex items-center gap-3" data-testid="impersonation-banner">
              <span className="flex-1">👤 {t("shell.impersonating", { name: impersonating })}</span>
              <button className="underline" onClick={stopImpersonating} data-testid="stop-impersonate-btn">{t("shell.stopImpersonating")}</button>
            </div>
          )}
          {announcements.map((a) => (
            <div key={a.id} className={`rounded-xl text-sm font-medium px-4 py-2.5 mb-3 ${a.level === "warning" ? "bg-pink/15 text-pink-ink" : "bg-gold/15 text-gold-ink"}`} data-testid={`announcement-${a.id}`}>
              📣 {lang === "fr" ? a.body_fr : a.body_en}
            </div>
          ))}
          {status === "past_due" && (
            <div className="rounded-xl bg-gold/15 text-gold-ink text-sm font-semibold px-4 py-2.5 mb-3 flex items-center gap-3" data-testid="billing-pastdue-banner">
              <span className="flex-1">⏳ {t("billing.pastDueBanner", { days: billing?.grace_days_left ?? 0 })}</span>
              <button className="underline shrink-0" onClick={() => navigate("/app/settings/subscription?step=3")}>{t("billing.renewNow")}</button>
            </div>
          )}
          {status === "read_only" && (
            <div className="rounded-xl bg-pink/15 text-pink-ink text-sm font-semibold px-4 py-2.5 mb-3 flex items-center gap-3" data-testid="billing-readonly-banner">
              <span className="flex-1">🔒 {t("billing.readOnlyBanner")}</span>
              <button className="underline shrink-0" onClick={() => navigate("/app/settings/subscription?step=3")}>{t("billing.renewNow")}</button>
            </div>
          )}
          <Outlet context={{ workspace: current }} />
          {upsell && (
            <div className="fixed bottom-24 md:bottom-4 left-1/2 -translate-x-1/2 z-[70] w-[92%] max-w-md rounded-2xl bg-surface border border-gold/40 p-5" data-testid="upsell-card">
              <div className="flex items-start gap-3">
                <Sparkles size={20} className="text-gold-ink shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-heading text-lg text-ink">
                    {upsell.code === "read_only" ? t("billing.readOnlyBanner") : t(`feat.${upsell.feature}`)}
                  </p>
                  {upsell.code !== "read_only" && upsell.plan_needed && (
                    <p className="text-sm text-ink/70 mt-0.5">{t("billing.upsellDesc", { plan: upsell.plan_needed === "big_padi" ? "Big Padi" : "Padi" })}</p>
                  )}
                </div>
                <button className="text-ink/60" onClick={() => setUpsell(null)} data-testid="upsell-close-btn"><X size={16} /></button>
              </div>
              <BpButton className="w-full mt-4" onClick={() => { setUpsell(null); navigate(`/app/settings/subscription${upsell.plan_needed ? `?plan=${upsell.plan_needed}` : ""}`); }} data-testid="upsell-cta-btn">
                {t("billing.seeplans")}
              </BpButton>
            </div>
          )}
        </main>
        <BottomTabs kind={kind} badges={badges} />
      </div>
    </div>
  );
}
